import React, { useEffect, useState } from "react";
import { req, tr, L } from "./api";
import { BiText, BiLines, Field, Check, Msg } from "./fields";

const PRACTICES = ["capital", "ma", "valuation", "project", "sustainable", "restructuring", "entry", "strategy", "family", "cfo"];
const SECTORS = ["rmg", "consumer", "pharma", "infra", "energy", "agro", "financial", "technology"];

const blankCase = () => ({ order: 99, practice: "capital", sector: "rmg", year: "2026", featured: false, illustrative: true, published: true,
  title: L(), client: L(), location: L(), value: L(), summary: L(), challenge: L(), approach: L([] , []), tech: L(), results: [] });
const blankIns = () => ({ order: 99, date: new Date().toISOString().slice(0, 10), readMins: 4, published: true,
  topic: L(), title: L(), summary: L(), body: { en: [], bn: [] } });

function ItemsAdmin({ coll, blank, canEdit, renderForm, columns, title }) {
  const [items, setItems] = useState(null);
  const [edit, setEdit] = useState(null); // null | "new" | id
  const [draft, setDraft] = useState(null);
  const [msg, setMsg] = useState("");

  const load = () => req(`/admin/${coll}`).then((r) => setItems(r.sort((a, b) => (a.order || 99) - (b.order || 99)))).catch((e) => setMsg(e.message));
  useEffect(() => { load(); }, [coll]);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (edit === "new") await req(`/admin/${coll}`, { method: "POST", body: draft });
      else await req(`/admin/${coll}/${edit}`, { method: "PUT", body: draft });
      setMsg("Saved."); setEdit(null); setDraft(null); load();
    } catch (x) { setMsg("Could not save: " + x.message); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this item? This cannot be undone.")) return;
    try { await req(`/admin/${coll}/${id}`, { method: "DELETE" }); setMsg("Deleted."); load(); }
    catch (x) { setMsg("Could not delete: " + x.message); }
  };

  if (edit) {
    return (
      <form className="form" onSubmit={save} data-testid={`${coll}-edit-form`}>
        <Msg msg={msg} />
        {renderForm(draft, setDraft)}
        <div className="row-actions" style={{ gridColumn: "1/-1" }}>
          <button type="submit" className="btn" data-testid={`${coll}-save`}>Save</button>
          <button type="button" className="btn ghost" onClick={() => { setEdit(null); setDraft(null); }} data-testid={`${coll}-cancel`}>Cancel</button>
        </div>
      </form>
    );
  }
  if (!items) return <p className="muted">Loading…</p>;
  return (
    <div>
      <Msg msg={msg} />
      {canEdit && <div className="row-actions" style={{ marginBottom: 18 }}>
        <button className="btn sm" onClick={() => { setEdit("new"); setDraft(blank()); }} data-testid={`${coll}-new`}>Add new</button>
      </div>}
      {items.length ? (
        <div className="tbl-wrap"><table className="tbl" data-testid={`${coll}-table`}>
          <thead><tr><th>{title}</th>{columns.map((c) => <th key={c[0]}>{c[0]}</th>)}<th>Status</th>{canEdit && <th></th>}</tr></thead>
          <tbody>{items.map((x) => (
            <tr key={x.id}>
              <td><strong>{tr(x.title)}</strong>{!tr(x.title, "bn") && <div className="bn-miss">Bangla missing</div>}</td>
              {columns.map((c) => <td key={c[0]}>{c[1](x)}</td>)}
              <td><span className={`pill${x.published !== false ? " on" : ""}`}>{x.published !== false ? "Published" : "Draft"}</span></td>
              {canEdit && <td><div className="row-actions">
                <button className="btn sm ghost" onClick={() => { setEdit(x.id); setDraft(JSON.parse(JSON.stringify(x))); }} data-testid={`${coll}-edit-${x.id}`}>Edit</button>
                <button className="btn sm ghost danger" onClick={() => del(x.id)} data-testid={`${coll}-del-${x.id}`}>Delete</button>
              </div></td>}
            </tr>))}</tbody></table></div>
      ) : <div className="empty">Nothing here yet.</div>}
    </div>
  );
}

export function CasesAdmin({ canEdit }) {
  return (
    <ItemsAdmin coll="cases" blank={blankCase} canEdit={canEdit} title="Case study"
      columns={[["Practice", (x) => x.practice], ["Year", (x) => x.year], ["Flags", (x) => <span>{x.featured && <span className="pill on">Home</span>} {x.illustrative && <span className="pill warn">Illustrative</span>}</span>]]}
      renderForm={(d, setD) => {
        const up = (k, v) => setD({ ...d, [k]: v });
        const upRes = (i, k, v) => { const r = [...(d.results || [])]; r[i] = { ...r[i], [k]: v }; up("results", r); };
        return (<>
          <div className="grp">Case study</div>
          <BiText label="Title" value={d.title} onChange={(v) => up("title", v)} />
          <BiText label="Client description (anonymised)" value={d.client} onChange={(v) => up("client", v)} />
          <Field label="Practice"><select value={d.practice} onChange={(e) => up("practice", e.target.value)} data-testid="case-practice">{PRACTICES.map((p) => <option key={p} value={p}>{p}</option>)}</select></Field>
          <Field label="Sector"><select value={d.sector} onChange={(e) => up("sector", e.target.value)}>{SECTORS.map((s) => <option key={s} value={s}>{s}</option>)}</select></Field>
          <Field label="Year"><input value={d.year || ""} onChange={(e) => up("year", e.target.value)} /></Field>
          <Field label="Display order (lower first)"><input type="number" value={d.order ?? 99} onChange={(e) => up("order", Number(e.target.value))} /></Field>
          <BiText label="Transaction size" value={d.value} onChange={(v) => up("value", v)} />
          <BiText label="Location" value={d.location} onChange={(v) => up("location", v)} />
          <BiText label="Summary" value={d.summary} onChange={(v) => up("summary", v)} area />
          <BiText label="The challenge" value={d.challenge} onChange={(v) => up("challenge", v)} area />
          <BiLines label="Our approach (steps)" value={d.approach} onChange={(v) => up("approach", v)} />
          <BiText label="Technology used" value={d.tech} onChange={(v) => up("tech", v)} area />
          <div className="grp">Results</div>
          {(d.results || []).map((r, i) => (
            <React.Fragment key={i}>
              <BiText label={`Result ${i + 1} — number`} value={r.v} onChange={(v) => upRes(i, "v", v)} />
              <BiText label={`Result ${i + 1} — label`} value={r.l} onChange={(v) => upRes(i, "l", v)} />
              <div className="row-actions" style={{ gridColumn: "1/-1" }}>
                <button type="button" className="btn sm ghost danger" onClick={() => up("results", d.results.filter((_, j) => j !== i))}>Remove result {i + 1}</button>
              </div>
            </React.Fragment>))}
          <div className="row-actions" style={{ gridColumn: "1/-1" }}>
            <button type="button" className="btn sm ghost" onClick={() => up("results", [...(d.results || []), { v: L(), l: L() }])} data-testid="case-add-result">Add a result</button>
          </div>
          <div className="grp">Display</div>
          <Check label="Published on the site" checked={d.published !== false} onChange={(v) => up("published", v)} testid="case-published" />
          <Check label="Feature on the home page" checked={d.featured} onChange={(v) => up("featured", v)} testid="case-featured" />
          <Check label="Illustrative example (shows a label)" checked={d.illustrative} onChange={(v) => up("illustrative", v)} testid="case-illustrative" />
        </>);
      }} />
  );
}

export function InsightsAdmin({ canEdit }) {
  return (
    <ItemsAdmin coll="insights" blank={blankIns} canEdit={canEdit} title="Insight"
      columns={[["Topic", (x) => tr(x.topic)], ["Date", (x) => x.date]]}
      renderForm={(d, setD) => {
        const up = (k, v) => setD({ ...d, [k]: v });
        const upBody = (lang, text) => up("body", { ...(d.body || {}), [lang]: text.split(/\n\s*\n/).map((x) => x.replace(/\s+/g, " ").trim()).filter(Boolean) });
        return (<>
          <div className="grp">Insight</div>
          <BiText label="Title" value={d.title} onChange={(v) => up("title", v)} />
          <BiText label="Topic" value={d.topic} onChange={(v) => up("topic", v)} />
          <Field label="Date"><input type="date" value={d.date || ""} onChange={(e) => up("date", e.target.value)} /></Field>
          <Field label="Minutes to read"><input type="number" value={d.readMins || 4} onChange={(e) => up("readMins", Number(e.target.value))} /></Field>
          <Field label="Display order (lower first)"><input type="number" value={d.order ?? 99} onChange={(e) => up("order", Number(e.target.value))} /></Field>
          <BiText label="Summary" value={d.summary} onChange={(v) => up("summary", v)} area />
          <div className="bi">
            <div className="bi-h">Article text (blank line between paragraphs)</div>
            <div className="field"><label className="lbl">English</label><textarea rows={10} value={(d.body?.en || []).join("\n\n")} onChange={(e) => upBody("en", e.target.value)} data-testid="insight-body-en" /></div>
            <div className="field"><label className="lbl">বাংলা</label><textarea rows={10} value={(d.body?.bn || []).join("\n\n")} onChange={(e) => upBody("bn", e.target.value)} /></div>
          </div>
          <Check label="Published on the site" checked={d.published !== false} onChange={(v) => up("published", v)} testid="insight-published" />
        </>);
      }} />
  );
}
